import React, { createContext, useState, ReactNode, useContext } from 'react';
import {
  CategoryDataType,
  CriterionDataType,
  AddOrRemoveCriterionAction,
  SetCriteriaConjuctionAction,
  ToggleCriterionIncExcAction,
  SetTenureAction
} from '@recone/models';
import { initialFilters } from 'components/FiltersComponent/initial-filters';

interface IFilterContext {
  categories: CategoryDataType[];
  setCategories: React.Dispatch<React.SetStateAction<CategoryDataType[]>>;
  setCategoryConjuction: (categoryName: string, value: 'AND' | 'OR') => void;
  setCriteria: (categoryName: string, filterName: string, newCriteria: CriterionDataType[], isPast?: boolean) => void;
  addCriteria: AddOrRemoveCriterionAction;
  removeCriteria: AddOrRemoveCriterionAction;
  toggleCriterionIncExc: ToggleCriterionIncExcAction;
  setCriteriaConjuction: SetCriteriaConjuctionAction;
  upsertCriteria: AddOrRemoveCriterionAction;
  setTenure: SetTenureAction;
  handleClearFilters: () => void;
}

const FilterContext = createContext<IFilterContext>({} as IFilterContext);

const FilterProvider = ({ children }: { children: ReactNode }) => {
  const [categories, setCategories] = useState<CategoryDataType[]>(JSON.parse(JSON.stringify(initialFilters))); // Clone initialFilters to avoid mutation issues

  const handleClearFilters = () => {
    setCategories(JSON.parse(JSON.stringify(initialFilters)));
   };

  const setCategoryConjuction = (categoryName: string, value: 'AND' | 'OR') => {
    const updatedCategories = [...categories];
    const category = updatedCategories.find((c) => c.name === categoryName);
    if (category && category.conjuction !== value) {
      category.conjuction = value;
      setCategories(updatedCategories);
    }
  };

  const addCriteria = (categoryName: string, filterName: string, value: string, identifier?: string) => {
    const updatedCategories = [...categories];
    const category = updatedCategories.find((c) => c.name === categoryName);
    if (!category) {
      return;
    }
    const filter = category.filters.find((f) => f.name === filterName);
    if (!filter) {
      return;
    }
    if (filter.criteria.some((c) => (identifier ? c.identifier === identifier : c.value === value))) {
      return;
    }

    const criterion: CriterionDataType = { value, included: true };
    if (identifier) {
      criterion.identifier = identifier;
    }
    filter.criteria.push(criterion);

    setCategories(updatedCategories);
  };

  const removeCriteria = (categoryName: string, filterName: string, value: string, identifier?: string) => {
    const updatedCategories = [...categories];
    const category = updatedCategories.find((c) => c.name === categoryName);
    if (!category) {
      return;
    }
    const filter = category.filters.find((f) => f.name === filterName);
    if (!filter) {
      return;
    }

    const criterion = filter.criteria.find((c) => (identifier ? c.identifier === identifier : c.value === value));
    if (!criterion) {
      return;
    }

    filter.criteria.splice(filter.criteria.indexOf(criterion), 1);

    setCategories(updatedCategories);
  };

  const setCriteria = (
    categoryName: string,
    filterName: string,
    newCriteria: CriterionDataType[],
    isPast?: boolean
  ) => {
    const updatedCategories = [...categories];
    const categoryToUpdate = updatedCategories.find((category) => category.name === categoryName);
    if (!categoryToUpdate) {
      return;
    }
    const filterToUpdate = categoryToUpdate.filters.find((filter) => filter.name === filterName);
    if (!filterToUpdate) {
      return;
    }
    filterToUpdate.past = isPast;
    filterToUpdate.criteria = [...newCriteria];

    setCategories(updatedCategories);
  };

  const toggleCriterionIncExc = (categoryName: string, filterName: string, value: string) => {
    const updatedCategories = [...categories];
    const category = updatedCategories.find((c) => c.name === categoryName);
    if (!category) {
      return;
    }
    const filter = category.filters.find((f) => f.name === filterName);
    if (!filter) {
      return;
    }

    const criterion = filter.criteria.find((c) => c.value === value);
    if (!criterion) {
      return;
    }

    criterion.included = !criterion.included;

    setCategories(updatedCategories);
  };

  const setCriteriaConjuction = (categoryName: string, filterName: string, conjunction: boolean) => {
    const updatedCategories = [...categories];
    const category = updatedCategories.find((c) => c.name === categoryName);
    if (!category) {
      return;
    }
    const filter = category.filters.find((f) => f.name === filterName);
    if (!filter) {
      return;
    }

    filter.overrideConjunction = conjunction;
    setCategories(updatedCategories);
  };

  const upsertCriteria = (categoryName: string, filterName: string, value: string, identifier?: string) => {
    const updatedCategories = [...categories];
    const category = updatedCategories.find((c) => c.name === categoryName);
    if (!category) {
      return;
    }
    const filter = category.filters.find((f) => f.name === filterName);
    if (!filter) {
      return;
    }

    let criterion = filter.criteria.find((c) => c.identifier === identifier);
    if (criterion) {
      criterion.value = value;
    } else {
      filter.criteria.push({ value, identifier, included: true });
    }

    setCategories(updatedCategories);
  };

  const setTenure = (categoryName: string, filterName: string, isEnabled: boolean, tenure: number) => {
    const updatedCategories = [...categories];
    const category = updatedCategories.find((c) => c.name === categoryName);
    if (!category) {
      return;
    }
    const filter = category.filters.find((f) => f.name === filterName);
    if (!filter) {
      return;
    }
    filter.tenure = isEnabled ? tenure : undefined;
    setCategories(updatedCategories);
  };

  const contextValue: IFilterContext = {
    categories,
    setCategories,
    setCategoryConjuction,
    addCriteria,
    removeCriteria,
    setCriteria,
    toggleCriterionIncExc,
    setCriteriaConjuction,
    upsertCriteria,
    handleClearFilters,
    setTenure
  };

  return <FilterContext.Provider value={contextValue}>{children}</FilterContext.Provider>;
};

export { FilterContext, FilterProvider };